<template>
    <div class="card flex-grow-1 chat-panel-card">
        <div
            class="card-body panel-card-body py-0 d-flex flex-column global-theme-text my-1 conversation-container"
            ref="conversationContainer"
        >
            <div class="py-1"></div>
            <chat-conversation :messages="conversationMessages" :enable-audio="true"></chat-conversation>
        </div>
        <div class="chat-input-area px-3 pb-3">
            <div class="chat-input-shell" :class="{ 'chat-input-shell-disabled': inputLocked }">
                <textarea
                    type="text"
                    class="chat-input"
                    :placeholder="labelsTranslated.saySomethingHere + '...'"
                    v-model="inputText"
                    v-on:keydown.enter.exact.prevent="submitChatInput"
                    v-on:input="autoExpandInput"
                    maxlength="2000"
                    :disabled="inputLocked"
                    ref="textinput"
                    rows="1"
                ></textarea>
                <div
                    class="icon icon-glyphicons-basic-circle-up chat-submit-btn align-middle"
                    :class="{ 'chat-submit-btn-disabled': inputLocked || !inputText }"
                    @click="submitChatInput"
                    role="button"
                    :disabled="inputLocked || !inputText"
                ></div>
            </div>
        </div>
        <div
            v-show="submitIsAnalyzing || savedResponseHasAiFeedback"
            class="chat-feedback-area px-3 pb-3 pt-2"
        >
            <transition name="analyzing-fade">
                <div v-if="submitIsAnalyzing" class="d-flex mb-2">
                    <span class="small font-weight-500 flex-grow-0 text-light-wave">
                        Analyzing your conversation...
                    </span>
                </div>
            </transition>
            <div class="collapse" :id="'feedback-' + _uid" :class="{ show: forceShowFeedback }">
                <text-response-ai-feedback
                    v-if="savedResponseHasAiFeedback"
                    :panel="panel"
                    :response="savedResponse"
                ></text-response-ai-feedback>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import ChatConversation from "@/components/ChatConversation"
import TextResponseAiFeedback from "@/components/TextResponseAiFeedback"

export default {
    name: "CustomGptPanel",
    components: {
        ChatConversation,
        TextResponseAiFeedback,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        panel: {
            type: Object,
            required: true,
        },
        sessionId: {
            type: [String, Number],
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        locked: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            inputText: "",
            avatarThinking: false,
            conversationMessages: [],
            // When true, the AI feedback section is rendered already-expanded (no slide-down
            // animation). Used when reloading a panel that has previously-submitted feedback.
            forceShowFeedback: false,
        }
    },
    computed: {
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        // A set of correct intent IDs discovered.
        correctIntentIdSet() {
            // Extract the list of conversation messages into a unique list of IDs representing
            // correct intents that were discovered.
            return this.conversationMessages.reduce((accumulator, message) => {
                if (message.intents_discovered) {
                    message.intents_discovered.forEach((intent) => {
                        // Add the intent ID if it's correct.
                        if (intent.correct) {
                            accumulator.add(intent.chat_agent_intent.id)
                        }
                    })
                }
                return accumulator
            }, new Set())
        },
        // The count of discovered correct intents.
        correctIntentCount() {
            return Array.from(this.correctIntentIdSet).length
        },
        // Returns true if there's only one column on the current node (used for styling).
        isSingleColumnNode() {
            return this.node.columns.length === 1
        },
        savedResponse() {
            if (!this.savedResponses || !this.savedResponses.length) {
                return null
            }
            return this.savedResponses[this.savedResponses.length - 1]
        },
        conversationHasContent() {
            return Boolean(this.conversationMessages.find((m) => m.type === "learner" && !m.isInitialMessagePlaceholder))
        },
        isSubmitted() {
            // A chat conversation is persisted server-side as the learner converses
            // (via SubmitChatInput), so the presence of a saved response alone doesn't
            // indicate submission. AI feedback is only populated once the conversation
            // has been finalized via the submit flow, so we use that as the discriminator.
            return this.savedResponseHasAiFeedback
        },
        savedResponseHasAiFeedback() {
            return this.savedResponse && JSON.stringify(this.savedResponse.ai_gen_feedback) !== "{}"
        },
        submitIsAnalyzing() {
            return this.$store.getters.submitIsLoading
        },
        inputLocked() {
            return this.locked || (!this.panel.allow_answer_changing && this.isSubmitted)
        },
    },
    watch: {
        correctIntentCount(value) {
            this.$emit("update:correctResponsesCount", value)
        },
        conversationHasContent(value) {
            this.$emit("update:isComplete", value)
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
            if (value) {
                this.$nextTick(() => {
                    $(`#feedback-${this._uid}`).collapse("show")
                })
            }
        },
    },
    methods: {
        submitChatInput(event) {
            if (event && event.shiftKey) {
                // Let shift+enter pass through for line breaks.
                return
            }
            if (this.inputLocked) {
                return
            }
            if (!this.inputText) {
                return
            }
            this.getChatResponse()
        },
        // Submit the learner's text input to the GPT.
        getChatResponse() {
            if (this.conversationMessages.length && this.conversationMessages[0].isInitialMessagePlaceholder) {
                // Remove the initial conversation message placeholder from the conversation,
                // so that it's replaced by the 'real' first message.
                this.conversationMessages.splice(0, 1)
            }

            if (this.inputText) {
                // Add the learner's message to the list of messages immediately.
                this.conversationMessages.push({
                    type: "learner",
                    content: this.inputText,
                })
            }

            // Make sure the most recent input is visible.
            this.scrollChatToBottomNextTick()
            let savedInputText = this.inputText
            // Clear the message input field.
            this.inputText = ""
            // Reset the height of the input field.
            let vm = this
            this.$nextTick(() => {
                vm.autoExpandInput()
            })
            // Add a response message immediately in the 'thinking' state.
            let responseMessage = {
                type: "system",
                content: "",
                isThinking: true,
            }
            vm.conversationMessages.push(responseMessage)
            http.post(`/api/students/sessions/${this.sessionId}/panel/${this.panel.id}/chat/`, {
                inputText: savedInputText,
            })
                .then((response) => {
                    // Add the response content to the message.
                    responseMessage.content = response.data.content
                    responseMessage.audio = response.data.audio_file
                    let intentsDiscovered = response.data.intents_discovered.map((i) => {
                        return {
                            ...i,
                            // Add a flag on each discovered intent representing if it's new or not.
                            isNewDiscovery: !vm.correctIntentIdSet.has(i.chat_agent_intent.id),
                        }
                    })
                    vm.$set(responseMessage, "intents_discovered", intentsDiscovered)
                    responseMessage.isThinking = false
                    // Scroll to the bottom of the conversation again to make the system response visible.
                    vm.scrollChatToBottomNextTick()
                    vm.$emit("response-received")
                })
                .catch(() => {
                    responseMessage.isThinking = false
                })
        },
        // Scroll to the bottom of the chat conversation on the next render.
        scrollChatToBottomNextTick() {
            this.$nextTick(() => {
                let div = this.$refs.conversationContainer
                div.scrollTop = div.scrollHeight
            })
        },
        // Expand the height of a learner input field to fit its contents.
        autoExpandInput() {
            let input = this.$refs.textinput
            // Reset the height to shrink back if text is deleted.
            input.style.height = "auto"
            // Set the height to the scrollHeight to remove the scrollbar, up to a max height.
            input.style.height = Math.min(132, input.scrollHeight) + "px"
        },
    },
    mounted() {
        if (this.conversationMessages.length) {
            // Don't initialize the state if there are already conversation messages present.
            // This fixes an issue where toggling edit mode clears the conversation history.
            return
        }
        if (this.savedResponses.length) {
            let lastConversation = this.savedResponses[this.savedResponses.length - 1]
            this.conversationMessages = lastConversation.messages
            if (this.savedResponseHasAiFeedback) {
                this.forceShowFeedback = true
            }
            this.scrollChatToBottomNextTick()
        } else if (this.panel.conversation_start_mode === "ai") {
            // The panel is configured to have the AI start, so fetch an initial message
            // from the AI.
            this.getChatResponse()
        } else {
            // Add an initial message to be displayed to invite the learner to start the chat.
            this.conversationMessages.push({
                type: "learner",
                content: this.labelsTranslated.youConversationWillAppearHere,
                isInitialMessagePlaceholder: true,
            })
        }
        this.$emit("update:isSubmitted", this.isSubmitted)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/chat.scss";

.conversation-container {
    flex: 1 1 0;
    min-height: 400px;
    overflow-y: auto;
}

.chat-input-area,
.chat-feedback-area {
    flex-shrink: 0;
}

.chat-input-shell {
    display: flex;
    align-items: flex-end;
    background-color: $input-bg;
    border: $input-border-width solid $input-border-color;
    border-radius: 1.25rem;
    padding: 4px 4px 4px 14px;
    transition: $transition-base;

    &:focus-within {
        border-color: $input-focus-border-color;
        box-shadow: $input-focus-box-shadow;
    }

    &.chat-input-shell-disabled {
        background-color: $gray-100;
    }
}

.chat-input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    resize: none;
    scrollbar-width: none;
    padding: 8px 4px 8px 0;
    color: $gray-900;
    font-size: 1rem;
    line-height: 1.4;

    &:focus {
        outline: 0;
        box-shadow: none;
    }

    &::placeholder {
        color: $gray-500;
    }

    &:disabled {
        background: transparent;
        color: $gray-700;
    }
}

.chat-submit-btn {
    color: $primary;
    font-size: 40px;
    line-height: 1;
    flex-shrink: 0;
    cursor: pointer;
    transition: $transition-base;

    &:hover {
        color: #0e50ba;
    }
}

.chat-submit-btn-disabled {
    opacity: $btn-disabled-opacity;
    color: #0e50ba !important;
    cursor: default;

    &:hover {
        color: #0e50ba;
    }
}

.analyzing-fade-enter-active {
    transition: opacity 0.25s ease-in-out;
}

.analyzing-fade-enter {
    opacity: 0;
}
</style>